import type { FeatureCode } from '@/api/generated/enums';
import { isBuilt } from '@/pages/builtKeys';
import { menu, type MenuNode } from '@/shell/menu';

import type { FeatureOverride, Plan } from './api';

/**
 * What a company can see — its "menus" — is computed, never stored: a module shows when the package
 * includes its feature (or an override grants it) and the person's role allows it. This is the
 * panel's half of that: the package's features, each company's overrides on top, and an honest
 * marker on every switch that nothing reads yet.
 *
 * <p>
 * <strong>"No screens yet" is derived, not written down.</strong> A feature has screens when a built
 * menu entry carries it — `menu.ts` and `builtKeys.ts`, the same two lists the shell uses to say "not
 * built yet". The coded packages promise modules that do not exist (Basic includes `Pos`), and a
 * switch with nothing behind it is the easiest thing in the world to sell by accident. `MultiBranch`
 * is the sharp case: a company's branch count is capped by its package's branch <em>limit</em>, and
 * no screen or endpoint reads the feature at all.
 * </p>
 */

/** A wording map over a generated enum: `Record` makes a new member a compile error, the test an empty label. */
export const moduleLabels: Record<FeatureCode, string> = {
  MasterData: 'Catalogue and masters',
  UnitInventory: 'Garments and stock intake',
  Availability: 'Availability and calendar',
  RentalOrders: 'Bookings',
  ReturnInspection: 'Returns and inspection',
  Laundry: 'Cleaning',
  Billing: 'Invoices',
  Alterations: 'Alterations',
  Packages: 'Packages and sets',
  Fittings: 'Fittings',
  CustomerRisk: 'Customer risk and KYC',
  MultiBranch: 'Multiple branches',
  InterBranchTransfer: 'Inter-branch transfers',
  Pos: 'Point of sale',
  Purchase: 'Purchasing',
  Delivery: 'Delivery and pickup',
  CashAndExpense: 'Cash and expenses',
  Gst: 'GST returns',
  Payroll: 'Payroll',
  AssetManagement: 'Assets',
  UnitRoiAnalytics: 'Garment ROI',
  AdvancedReporting: 'Advanced reports',
  SchemeEngine: 'Schemes',
  ExcelImport: 'Excel import',
  TallyExport: 'Tally export',
  WhatsAppNotifications: 'WhatsApp notifications',
};

/** The features some built screen is gated on. A child entry inherits its parent's feature. */
export function featuresWithScreens(
  nodes: MenuNode[] = menu,
  built: (key: string) => boolean = isBuilt,
): Set<FeatureCode> {
  const found = new Set<FeatureCode>();

  const walk = (list: MenuNode[], inherited: FeatureCode | undefined) => {
    for (const node of list) {
      const feature = node.feature ?? inherited;
      if (feature && built(node.key)) found.add(feature);
      if (node.children) walk(node.children, feature);
    }
  };

  walk(nodes, undefined);
  return found;
}

export interface ModuleRow {
  feature: FeatureCode;
  label: string;
  inPackage: boolean;
  /** The override in force, if any — a lapsed one is shown as history, not as the answer. */
  override: FeatureOverride | undefined;
  effective: boolean;
  hasScreens: boolean;
}

/**
 * One row per feature: in the package, overridden, and what the company actually gets. An override
 * that has expired no longer decides anything, so the package decides again — the server's rule
 * (`IsInEffect`), read rather than restated.
 */
export function moduleRows(
  plan: Plan | undefined,
  overrides: FeatureOverride[],
  withScreens: Set<FeatureCode>,
  all: readonly FeatureCode[],
): ModuleRow[] {
  const inPackage = new Set(plan?.features ?? []);

  return all.map((feature) => {
    const override = overrides.find((o) => o.feature === feature && o.isInEffect);
    return {
      feature,
      label: moduleLabels[feature],
      inPackage: inPackage.has(feature),
      override,
      effective: override ? override.isGranted : inPackage.has(feature),
      hasScreens: withScreens.has(feature),
    };
  });
}

/**
 * The rows worth showing first: what the company can actually use, then what a switch could give it,
 * then what nothing reads yet — so the bottom of the list is where the dead switches live.
 */
export function orderRows(rows: ModuleRow[]): ModuleRow[] {
  const rank = (r: ModuleRow) => (r.hasScreens ? (r.effective ? 0 : 1) : 2);
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
}
